import { ProductTableItem } from '@/lib/types/core/product';
import { IBulkEditOperation } from '@/lib/types/core/product';
import { ProductTableItemUpdate, ProductTableState } from '@/lib/types/store/ProductState';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Initial state for the product table
 */
const initialState: ProductTableState = {
  // Data
  items: [],
  categories: [],

  // UI State
  selectedKeys: [],
  editingId: null,

  // Filter State
  filters: {},

  // Bulk Edit State
  bulkEditMode: false,
  bulkEditOperation: null,

  // Loading States
  loading: false,
  saving: false,
  deleting: false,

  // Pagination
  page: 1,
  pageSize: 10,
  totalItems: 0,

  // Sorting
  sortField: 'updatedAt',
  sortDirection: 'desc',

  // Error State
  error: null,
};

/**
 * Product Table Slice
 * Redux slice for the product table
 */
const productTableSlice = createSlice({
  name: 'productTable',
  initialState,
  reducers: {
    // Data Actions
    setProducts: (state, action: PayloadAction<ProductTableItem[]>) => {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    setCategories: (state, action: PayloadAction<any[]>) => {
      state.categories = action.payload;
    },

    // Single Product Actions
    updateProduct: (state, action: PayloadAction<ProductTableItemUpdate>) => {
      const { id, field, value } = action.payload;
      const index = state.items.findIndex(item => item.id === id);

      if (index !== -1) {
        // Special handling for variants to preserve existing variants
        if (field === 'variants' && state.items[index].variants) {
          // If value is null, keep existing variants
          if (!value) {
            return;
          }

          // If adding/updating variants, merge with existing
          state.items[index][field] = Array.isArray(value) ? value : state.items[index][field];
        } else {
          // Normal field update
          // @ts-expect-error - field is a valid key of ProductTableItem
          state.items[index][field] = value;
        }

        // Update status if published field is changed
        if (field === 'published') {
          state.items[index].status = value ? 'Published' : 'Draft';
        }
      }
    },

    // UI State Actions
    setSelectedKeys: (state, action: PayloadAction<string[]>) => {
      state.selectedKeys = action.payload;
    },
    setEditingId: (state, action: PayloadAction<string | null>) => {
      state.editingId = action.payload;
    },

    // Filter Actions
    setFilters: (state, action: PayloadAction<Record<string, any>>) => {
      state.filters = action.payload;
      // Reset to first page when filters change
      state.page = 1;
    },

    // Bulk Edit Actions
    setBulkEditMode: (state, action: PayloadAction<boolean>) => {
      state.bulkEditMode = action.payload;

      // Clear bulk edit operation when exiting bulk edit mode
      if (!action.payload) {
        state.bulkEditOperation = null;
      }
    },
    setBulkEditOperation: (state, action: PayloadAction<IBulkEditOperation | null>) => {
      state.bulkEditOperation = action.payload;
    },

    // Loading State Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.deleting = action.payload;
    },

    // Pagination Actions
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setTotalItems: (state, action: PayloadAction<number>) => {
      state.totalItems = action.payload;
    },

    // Sorting Actions
    setSortField: (state, action: PayloadAction<string>) => {
      state.sortField = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },

    // Error State Actions
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.saving = false;
      state.deleting = false;
    },

    // Reset Actions
    resetState: () => initialState,
  },
});

// Export actions
export const {
  setProducts,
  setCategories,
  updateProduct,
  setSelectedKeys,
  setEditingId,
  setFilters,
  setBulkEditMode,
  setBulkEditOperation,
  setLoading,
  setSaving,
  setDeleting,
  setPage,
  setPageSize,
  setTotalItems,
  setSortField,
  setSortDirection,
  setError,
  resetState,
} = productTableSlice.actions;

// Export reducer
export default productTableSlice.reducer;
